import React from 'react';
import { cn } from '../../lib/utils';

export type ProgressBarProps = {
  value: number;
  variant?: 'brand' | 'success' | 'warning' | 'danger';
  size?: 'sm' | 'md';
  showLabel?: boolean;
  segments?: number;
  className?: string;
};

const VARIANT_CLASSES: Record<NonNullable<ProgressBarProps['variant']>, string> = {
  brand: 'bg-brand',
  success: 'bg-status-success',
  warning: 'bg-status-warning',
  danger: 'bg-status-error',
};

const SIZE_CLASSES: Record<NonNullable<ProgressBarProps['size']>, string> = {
  sm: 'h-1.5',
  md: 'h-2.5',
};

const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    {
      value,
      variant = 'brand',
      size = 'md',
      showLabel = false,
      segments = 0,
      className,
    },
    ref,
  ) => {
    const clampedValue = Math.min(100, Math.max(0, value));
    const fillClass = VARIANT_CLASSES[variant];
    const sizeClass = SIZE_CLASSES[size];

    const renderBar = () => {
      if (segments > 0) {
        const segmentWidth = 100 / segments;
        const filledSegments = Math.floor((clampedValue / 100) * segments);
        const partialSegment = (clampedValue / 100) * segments - filledSegments;

        return (
          <div className="flex w-full gap-0.5">
            {Array.from({ length: segments }, (_, i) => {
              let width: number;
              if (i < filledSegments) {
                width = 100;
              } else if (i === filledSegments && partialSegment > 0) {
                width = partialSegment * 100;
              } else {
                width = 0;
              }
              return (
                <div
                  key={i}
                  className={cn(
                    'flex-1 min-w-0 rounded-full transition-all duration-500',
                    fillClass,
                  )}
                  style={{ width: `${width}%` }}
                />
              );
            })}
          </div>
        );
      }

      return (
        <div
          className={cn(
            'rounded-full transition-all duration-500',
            fillClass,
          )}
          style={{ width: `${clampedValue}%` }}
        />
      );
    };

    return (
      <div ref={ref} className={cn('w-full', className)}>
        <div
          className={cn(
            'w-full rounded-full overflow-hidden bg-surface-secondary',
            sizeClass,
          )}
        >
          {renderBar()}
        </div>
        {showLabel && (
          <span className="mt-1 block text-2xs text-content-tertiary">
            {Math.round(clampedValue)}%
          </span>
        )}
      </div>
    );
  },
);

ProgressBar.displayName = 'ProgressBar';

export default ProgressBar;
